const COOKIE_NAME = "googtrans";
const SCRIPT_SRC =
  "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
const HOST_ID = "google_translate_element";
const KEEP = "max-age=31536000";
const EXPIRE = "expires=Thu, 01 Jan 1970 00:00:00 GMT";

// Google's website translator still uses legacy codes for a few languages.
const TO_GOOGLE = { he: "iw" };
const FROM_GOOGLE = { iw: "he" };

const toGoogleCode = (code) => TO_GOOGLE[code] || code;
const fromGoogleCode = (code) => FROM_GOOGLE[code] || code;

const cookieDomains = () => {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [""];
  const isIp = /^\d+(\.\d+){3}$/.test(host);
  if (!isIp && parts.length >= 2) {
    domains.push(`.${parts.slice(-2).join(".")}`);
  }
  return domains;
};

const writeCookie = (value, lifetime) => {
  cookieDomains().forEach((domain) => {
    document.cookie = `${COOKIE_NAME}=${value}; path=/; ${lifetime}${
      domain ? `; domain=${domain}` : ""
    }`;
  });
};

export const getGoogleLanguage = () => {
  if (typeof document === "undefined") return "en";
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/[^/]*\/([^;]+)/);
  return match ? fromGoogleCode(decodeURIComponent(match[1])) : "en";
};

let loadPromise = null;

export const loadGoogleTranslate = () => {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    if (!document.getElementById(HOST_ID)) {
      const host = document.createElement("div");
      host.id = HOST_ID;
      host.className = "notranslate";
      document.body.appendChild(host);
    }

    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        { pageLanguage: "en", autoDisplay: false },
        HOST_ID
      );
      resolve(true);
    };

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onerror = () => {
      loadPromise = null;
      resolve(false);
    };
    document.body.appendChild(script);
  });

  return loadPromise;
};

const waitForCombo = (timeoutMs = 6000) =>
  new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const combo = document.querySelector("select.goog-te-combo");
      if (combo && combo.options.length > 1) return resolve(combo);
      if (Date.now() - started > timeoutMs) return resolve(null);
      setTimeout(tick, 150);
    };
    tick();
  });

/** Language codes offered by Google's widget (the page source language, English, is not included). */
export const getGoogleLanguageCodes = async () => {
  const loaded = await loadGoogleTranslate();
  const combo = loaded ? await waitForCombo(10000) : null;
  if (!combo) return [];
  return [...combo.options]
    .map((option) => option.value)
    .filter(Boolean)
    .map(fromGoogleCode);
};

const isPageTranslated = () => {
  const cls = document.documentElement.classList;
  return cls.contains("translated-ltr") || cls.contains("translated-rtl");
};

/**
 * Translate the whole page with Google Translate. English restores the original
 * text, which Google only does reliably with a reload.
 */
export const setGoogleLanguage = async (langCode) => {
  const target = langCode || "en";
  if (target === getGoogleLanguage()) return;

  const reloadOnce = () => {
    const key = `jcgn_gt_reload_${target}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    window.location.reload();
  };

  if (target === "en") {
    writeCookie("", EXPIRE);
    reloadOnce();
    return;
  }

  const googleCode = toGoogleCode(target);
  writeCookie(`/en/${googleCode}`, KEEP);

  const loaded = await loadGoogleTranslate();
  const combo = loaded ? await waitForCombo() : null;
  if (!combo) {
    reloadOnce();
    return;
  }

  const apply = () => {
    combo.value = googleCode;
    combo.dispatchEvent(new Event("change"));
  };
  apply();
  // The first change after the widget loads is sometimes ignored.
  setTimeout(() => {
    if (!isPageTranslated()) apply();
  }, 1000);
};
